// src/Room/RoomSpecifiedArticlesPanel.tsx
import React, { useEffect, useMemo, useRef } from "react";
import Resizable from "react-resizable-layout";
import { Box, Card, Divider, Group, SegmentedControl, Stack, Text, Tooltip } from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-enterprise";
import type { IRoomSpecifiedArticle } from "@backend/src/schemas";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { formatShortDate } from "../../globalUtils";
import { selectHttpSingleRoomFull } from "../../Room/roomSelectors";
import {
  selectSelectedSpecifiedArticlesResponsibility,
  setSelectedSpecifiedArticlesResponsibilityInRedux,
} from "../../Room/RoomSlice";
import { CompCodeName } from "../../components/CompCodeName";

type Row = IRoomSpecifiedArticle;

const SPLIT_STORAGE_KEY = "specifiedArticlesSplitY";

function uniqNonEmpty(arr: Array<string | undefined | null>) {
  const set = new Set<string>();
  for (const v of arr) {
    const s = (v ?? "").trim();
    if (!s) continue;
    set.add(s);
  }
  const collator = new Intl.Collator(undefined, { sensitivity: "base" });
  return Array.from(set).sort(collator.compare);
}

export const RoomSpecifiedArticlesPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const room = useAppSelector(selectHttpSingleRoomFull);
  const selectedResp = useAppSelector(selectSelectedSpecifiedArticlesResponsibility);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const initialPosition = useMemo(() => {
    if (typeof window === "undefined") return 260;
    const raw = window.localStorage.getItem(SPLIT_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n < 120) return 260;
    return n;
  }, []);

  const allRows = (room?.specifiedArticles ?? []) as Row[];

  const availableResponsibilities = useMemo(() => {
    return uniqNonEmpty(allRows.map((r) => r.responsibility));
  }, [allRows]);

  // Default: välj första responsibility om inget valt
  useEffect(() => {
    if (!selectedResp && availableResponsibilities.length > 0) {
      dispatch(setSelectedSpecifiedArticlesResponsibilityInRedux(availableResponsibilities[0]));
    }
  }, [dispatch, selectedResp, availableResponsibilities]);

  const filteredRows = useMemo(() => {
    if (!selectedResp) return allRows;
    return allRows.filter((r) => (r.responsibility ?? "").trim() === selectedResp);
  }, [allRows, selectedResp]);

  const syncedAtRaw = room?.specifiedArticlesSync?.syncedAt
    ? String(room.specifiedArticlesSync.syncedAt)
    : undefined;

  const syncedAtShort = syncedAtRaw ? formatShortDate(syncedAtRaw) : undefined;

  const columnDefs = useMemo<ColDef<Row>[]>(() => {
    return [
      {
        headerName: "CompCode",
        field: "compCode",
        flex: 1,
        minWidth: 180,
        cellRenderer: (params: any) => {
          const row = params.data as Row | undefined;
          return <CompCodeName compCode={row?.compCode} name={row?.name} />;
        },
      },
      {
        headerName: "Antall",
        field: "quantity",
        width: 120,
        type: "rightAligned",
        cellStyle: { fontWeight: 600 },
        valueFormatter: (p) => (typeof p.value === "number" ? String(p.value) : "-"),
      },
      // Skjult kolonne for søk / quick filter senere
      {
        headerName: "Navn",
        field: "name",
        hide: true,
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    []
  );

  return (
    <Card
      withBorder
      radius="md"
      w="100%"
      h="100%"
      style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      {/* Header */}
      <Stack gap="xs" style={{ flex: "0 0 auto" }}>
        <Group justify="space-between" align="center" wrap="nowrap">
          <Text fw={600}>Spesifiserte artikler</Text>

          <Stack gap={0} align="flex-end" style={{ minWidth: 0 }}>
            {syncedAtRaw ? (
              <Tooltip label={syncedAtRaw} position="bottom-end" withArrow>
                <Text size="xs" c="dimmed">
                  Synket {syncedAtShort}
                </Text>
              </Tooltip>
            ) : (
              <Text size="xs" c="dimmed">
                Ikke synket ennå
              </Text>
            )}
          </Stack>
        </Group>

        {availableResponsibilities.length > 0 && (
          <Box w="100%">
            <SegmentedControl
              value={selectedResp}
              onChange={(value) =>
                dispatch(setSelectedSpecifiedArticlesResponsibilityInRedux(value ? `${value}` : ""))
              }
              data={availableResponsibilities.map((d) => ({
                value: d,
                label: d, // fri streng: ARK/IARK/RUT/HF/...
              }))}
              radius="xl"
              size="sm"
              color="blue"
              transitionDuration={120}
            />
          </Box>
        )}

        {availableResponsibilities.length === 0 && (
          <Text size="sm" c="dimmed">
            Ingen responsibility/disiplin funnet (viser alle).
          </Text>
        )}
      </Stack>

      <Divider my="xs" />

      {/* Resizable content */}
      <Box ref={containerRef} style={{ flex: 1, minHeight: 0 }}>
        <Resizable
          axis="y"
          containerRef={containerRef}
          initial={initialPosition}
          min={120}
          onResizeEnd={(pos) => {
            if (typeof window !== "undefined") {
              window.localStorage.setItem(SPLIT_STORAGE_KEY, String(pos));
            }
          }}
        >
          {({ position, separatorProps }) => (
            <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
              {/* Top panel */}
              <div style={{ height: position, minHeight: 80, overflow: "hidden" }}>
                <div className="ag-theme-quartz" style={{ height: "100%", width: "100%" }}>
                  <AgGridReact<Row>
                    rowData={filteredRows}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    animateRows
                    suppressCellFocus
                  />
                </div>
              </div>

              {/* Separator */}
              <div {...separatorProps} style={{ flex: "0 0 auto" }}>
                <div
                  style={{
                    height: 6,
                    cursor: "row-resize",
                    background: "linear-gradient(to bottom, #e0e0e0, #c8c8c8, #e0e0e0)",
                    borderRadius: 4,
                  }}
                />
              </div>

              {/* Bottom panel (placeholder for kommentarer etc.) */}
              <div
                style={{
                  flex: 1,
                  minHeight: 0, // ✅ critical
                  padding: 12,
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  background: "#fafafa",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  <Stack gap="xs">
                    <Text fw={600}>Kommentarer</Text>
                    <Text size="sm" c="dimmed">
                      Kommer senere: kommentarer/avklaringer per compCode (og evt. responsibility).
                    </Text>

                    {/* TODO:
                        - liste kommentarer
                        - inputfelt + lagre
                        - koble til valgt responsibility + evt valgt rad i tabellen
                    */}
                  </Stack>
                </div>
              </div>
            </div>
          )}
        </Resizable>
      </Box>
    </Card>
  );
};
